import axios from 'axios';
import _ from 'lodash';

export const state = {
  vaultAssets: [],
  fromUserWallets: [],
  assetCreated: null,
  assetUpdated: null,
  selectedAsset: {},
  errorOnAssetCreated: null,
  errorOnAssetUpdated: null,
  toUserWallets: [],
  vaultAssetsPagination: {
    limit: 200,
    isLastPage: false,
    isFirstPage: true,
  },
  vaultAssetsNextPage: [],
  loading: false,
};


export const mutations = {
  setVaultAssets(context, vaultAssets) {
    const collator = new Intl.Collator([], { numeric: true });
    context.vaultAssets = vaultAssets.sort((a, b) => collator.compare(a.name, b.name));
    context.loading = false;
  },

  setVaultSelectedAsset(context, vaultAsset) {
    context.selectedAsset = vaultAsset;
    context.loading = false;
  },

  setVaultAssetsNextPage(context, nextUrl) {
    context.vaultAssetsNextPage = [...context.vaultAssetsNextPage, nextUrl];
    context.loading = false;
  },
  setAssetCreated(context, asset) {
    context.assetCreated = asset;
    context.loading = false;
  },
  setAssetUpdated(context, asset) {
    context.assetUpdated = asset;
    context.loading = false;
  },
  deleteAsset(context, token) {
    context.vaultAssets = context.vaultAssets.filter(asset => (asset.token !== token));
    context.loading = false;
  },
  setErrorOnAssetCreated(context, error) {
    context.errorOnAssetCreated = error;
    context.loading = false;
  },
  setErrorOnAssetUpdated(context, error) {
    context.errorOnAssetUpdated = error;
    context.loading = false;
  },
  clearVaultAssetsNextPage(context) {
    context.vaultAssetsNextPage = [];
    context.loading = false;
  },

  resetAsset(context) {
    context.vaultAssets = [];
    context.vaultAssetsNextPage = [];
    context.fromUserWallets = [];
    context.toUserWallets = [];
    context.vaultAssetsPagination = {
      limit: 200,
      isLastPage: false,
      isFirstPage: true,
    };
    context.loading = false;
  },

  setVaultAssetsbackPage(context) {
    context.vaultAssetsNextPage.pop();
    context.vaultAssetsNextPage.pop();
    context.loading = false;
  },

  setVaultAssetsPagination(context, pagination) {
    context.vaultAssetsPagination = pagination;
    context.loading = false;
  },

  setIsFirstPageAsset(context, value) {
    context.vaultAssetsPagination.isFirstPage = value;
  },

  setIsLastPageAsset(context, value) {
    context.vaultAssetsPagination.isLastPage = value;
  },

  setLimitItemsPerPageAsset(context, value) {
    context.vaultAssetsPagination.limit = value;
  },

  setLoading(context, value) {
    context.loading = value;
  },
};

export const getters = {
  getVaultAssetsPagination: currentState => currentState.vaultAssetsPagination,
  vaultSelectedAsset: currentState => currentState.selectedAsset,
  vaultErrorOnAssetCreated: currentState => currentState.errorOnAssetCreated,
  vaultErrorOnAssetUpdated: currentState => currentState.errorOnAssetUpdated,
  vaultAssetsTokens: currentState => currentState.vaultAssets,
};

export const actions = {
  async setLimitItemsPerPageAsset({ commit, dispatch }, limit) {
    commit('setLimitItemsPerPageAsset', limit);
    commit('clearVaultAssetsNextPage');
    dispatch('getVaultAssets');
  },

  async setVaultSelectedAsset({ commit }, asset) {
    commit('setVaultSelectedAsset', asset);
  },

  async backPageAsset({ commit, dispatch }) {
    commit('setVaultAssetsbackPage');
    dispatch('getVaultAssets', true);
  },

  async resetAsset({ commit }) {
    commit('resetAsset');
  },

  async deleteVaultAsset({ commit, dispatch, rootState }, token) {
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };

    const url = `${window.BASE_API.VAULT}/admin/assets/${token}`;
    await axios.delete(url, options);
    commit('deleteAsset', token);
    dispatch('resetAsset');
    dispatch('getVaultAssets');
  },
  async createVaultAsset({ commit, dispatch, rootState }, payload) {
    commit('setErrorOnAssetCreated', null);
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };
    const { token, name, project, amount } = payload;
    const url = `${window.BASE_API.VAULT}/admin/assets`;
    try {
      const createdAsset = await axios.post(url, {
        name,
        token,
        project,
        amount,
      }, options);
      commit('resetAsset');
      commit('setAssetCreated', createdAsset.data);
      dispatch('getVaultAssets');
      commit('setLoading', false);
    } catch (error) {
      console.error(error.message);
      commit('setLoading', false);
      commit('setErrorOnAssetCreated', error.message);
      throw error;
    }
  },
  async updateVaultAsset({ commit, dispatch, rootState }, payload) {
    commit('setErrorOnAssetUpdated', null);
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };
    const { token, name, project, amount, deleted } = payload;
    const url = `${window.BASE_API.VAULT}/admin/assets/update`;
    try {
      const updatedAsset = await axios.post(url, {
        name,
        token,
        project,
        amount,
        deleted,
      }, options);
      commit('resetAsset');
      commit('setAssetUpdated', updatedAsset.data);
      dispatch('getVaultAssets');
      commit('setLoading', false);
    } catch (error) {
      console.error(error.message);
      commit('setLoading', false);
      commit('setErrorOnAssetUpdated', error.message);
      throw error;
    }
  },
  async getVaultAssets({ commit, rootState, ...context }, forceLimit) {
    try {
      const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
      const options = {
        headers: {
          Authorization: jwtToken,
        },
      };

      commit('setLoading', true);
      const url = _.last(context.state.vaultAssetsNextPage) ?
        `${window.BASE_API.VAULT_BASE}${_.last(context.state.vaultAssetsNextPage)}` :
        `${window.BASE_API.VAULT}/admin/assets`;
      const vaultAssets = (await axios.get(url, options)).data;

      commit('setIsLastPageAsset', !vaultAssets.nextUrl);
      commit('setIsFirstPageAsset', context.state.vaultAssetsNextPage.length === 0);
      if (vaultAssets.nextUrl) {
        commit('setVaultAssetsNextPage', vaultAssets.nextUrl);
      }
      commit('setVaultAssets', vaultAssets.items);
      commit('setLoading', false);
    } catch (err) {
      commit('setLoading', false);
    }
  },
  async generateHoldersReport({ commit, rootState, ...context }, payload) {
    commit('setLoading', true);

    return axios.post(`${window.BASE_API.VAULT}/admin/reports/holdings`, payload)
      .finally(() => {
        commit('setLoading', false);
      });
  },
};

const vaultAssetsStore = {
  state,
  mutations,
  getters,
  actions,
};

export default vaultAssetsStore;



// WEBPACK FOOTER //
// ./src/store/vault/assetsStore.js